import React from 'react';
import { motion } from 'framer-motion';
import { Award, Clock, X, AlertTriangle, Check, Trophy, Brain } from 'lucide-react';

/**
 * Quiz Rules Component
 * Displays the rules and warnings for the academic challenge with VarsityLink styling
 */
const QuizRules = ({ onStart, onCancel }) => {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      className="bg-white rounded-xl shadow-lg p-6 max-w-lg mx-auto"
    >
      {/* Header */}
      <div className="flex justify-between items-start mb-6">
        <div className="flex items-center">
          <div className="p-2 bg-green-100 rounded-full mr-3">
            <Trophy className="text-green-600 w-5 h-5" />
          </div>
          <h2 className="text-xl font-bold text-gray-800">Academic Challenge Rules</h2>
        </div>
        
        {onCancel && (
          <button 
            onClick={onCancel} 
            className="text-gray-400 hover:text-gray-600 p-1"
            aria-label="Close rules"
          >
            <X size={20} />
          </button>
        )}
      </div>
      
      {/* Main Rule Cards */}
      <div className="space-y-4 mb-6">
        <RuleCard 
          icon={<Clock className="text-blue-600" />}
          color="blue"
          title="Time Limit"
          description="Each question has a 30-second time limit. If time runs out, the question is marked incorrect and you'll move to the next one."
        />
        
        <RuleCard 
          icon={<Brain className="text-purple-600" />}
          color="purple"
          title="Scoring System"
          description="10 points for each correct answer. With 10 questions total, a perfect score is 100 points."
        />
        
        <RuleCard 
          icon={<Trophy className="text-amber-600" />}
          color="amber"
          title="Leaderboard Ranking"
          description="Your scores contribute to both your personal ranking and your university's standing on the leaderboard."
        />
        
        <RuleCard 
          icon={<AlertTriangle className="text-red-600" />}
          color="red"
          title="Strict Completion Policy"
          description="You must complete all questions in a single session. If you exit early, you'll receive ZERO points for today's challenge."
          isWarning={true}
        />
      </div>
      
      {/* Daily Challenge Box */}
      <div className="bg-gray-50 p-4 rounded-lg mb-4">
        <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
          <Award className="mr-2 text-green-600" size={18} />
          Additional Information
        </h3>
        <ul className="text-sm text-gray-600 space-y-2">
          <li className="flex items-start">
            <Check size={16} className="mr-2 text-green-500 mt-0.5 flex-shrink-0" />
            <span>You can only take the challenge once per day</span>
          </li>
          <li className="flex items-start">
            <Check size={16} className="mr-2 text-green-500 mt-0.5 flex-shrink-0" />
            <span>Questions cover a variety of academic subjects</span>
          </li>
          <li className="flex items-start">
            <Check size={16} className="mr-2 text-green-500 mt-0.5 flex-shrink-0" />
            <span>Your scores automatically contribute to your university's ranking</span>
          </li>
          <li className="flex items-start">
            <Check size={16} className="mr-2 text-green-500 mt-0.5 flex-shrink-0" />
            <span>Higher scores earn more points for your profile achievements</span>
          </li>
        </ul>
      </div>
      
      {/* Action Buttons */}
      <div className="flex items-center justify-between">
        {onCancel && (
          <button 
            onClick={onCancel}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Not Now
          </button>
        )}
        
        <button 
          onClick={onStart}
          className="px-6 py-2.5 bg-gradient-to-r from-green-500 to-teal-500 text-white font-medium rounded-lg shadow-sm hover:from-green-600 hover:to-teal-600 transition-all"
        >
          I Understand, Start Challenge
        </button>
      </div>
    </motion.div>
  );
};

// Helper component for rule cards
const RuleCard = ({ icon, color, title, description, isWarning = false }) => {
  return (
    <div className={`p-4 rounded-lg ${
      isWarning 
        ? 'bg-red-50 border border-red-100' 
        : `bg-${color}-50 border border-${color}-100`
    }`}>
      <div className="flex">
        <div className={`p-2 rounded-full ${
          isWarning 
            ? 'bg-red-100' 
            : `bg-${color}-100`
        } mr-3 flex-shrink-0`}>
          {icon}
        </div>
        <div>
          <h3 className={`font-semibold mb-1 ${
            isWarning ? 'text-red-700' : `text-${color}-700`
          }`}>
            {title}
          </h3>
          <p className={`text-sm ${isWarning ? 'text-red-600' : 'text-gray-600'}`}>
            {description}
          </p>
        </div>
      </div>
    </div>
  );
};

export default QuizRules;